import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AI_DESCRIPTION_OFFSET, WS_SEARCH_TIMEOUT } from '../config';
import { toUrlFromQuery } from '../utils';
import {
  SearchAddressResult,
  SearchAgentResult,
  SearchAgentTeamResult,
} from '../system/search';
import {
  ListingRemarksHistoryResponse,
  ListingRemarksResponse,
  RecommendationResponse,
} from '../system/recommendations';
import { MemberDefaults } from '../system/defaults';
import { useAuth } from './AuthContext';
import { ResoResource } from '../system/reso';
import { ResoQueryBuilder, resource } from '../system/reso-query';
import { useDeleteListingResources } from './util/deleteListingResources';
import { DashboardData } from '../system/analytics';
import { sanitizeTaxId } from '../system/location';
import { useTenant } from './TenantContext';
import { useWebSocketContext } from './WebSocketContext';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { PropertyField } from '../system/field';
import { ProjectionResponse } from '../system/resource';
import {
  ListingContactsRequest,
  ListingContactsResponse,
  ShareDraftRequest,
  ShareDraftResponse,
} from '../components/edit/hooks/useShareDraft/types';
import { isMimeTypeNullError } from '../system/media';
import { SaveAccountResponse, AccountMetadata } from '../system/account';
import { extractCreateParams } from './util/createParams';
import { SystemHistoryResult } from '../system/history';
import { SupraTask } from '../system/lockbox';

/**
 * Media upload mode - determines how media replacement is handled
 */
export type MediaUploadMode = 'standard' | 'switch_a_roo' | 'replace_binary';

/**
 * Original metadata to copy when using switch_a_roo or replace_binary mode
 */
export interface OriginalMediaMetadata {
  displayOrder?: number;
  shortDescription?: string;
  longDescription?: string;
  visibility?: string;
}

// Media upload initialization types for bulk operations
export interface MediaUploadRequest {
  fileName: string;
  fileSize: number;
  fileType: string;
  category: string;
  listingKey: string;
  /** Optional listingId - improves AI performance by avoiding RESO lookup */
  listingId?: string;
  /** MLS item number */
  mediaItemNumber?: number;
  /** Display sort order */
  mediaDisplayOrder?: number;
  /** Media visibility (e.g., 'Public', 'IDX', 'Private', 'AgentOnly') */
  visibility?: string;
  scanForObjects?: boolean;
  generateDescriptions?: boolean;
  /** WebSocket connectionId for real-time updates (from WHOAMI) */
  connectionId?: string;
  /** Upload mode for replacement handling */
  uploadMode?: MediaUploadMode;
  /** Media key being replaced (for switch_a_roo and replace_binary modes) */
  replacingMediaKey?: string;
  /** Original metadata to copy (for switch_a_roo and replace_binary modes) */
  originalMetadata?: OriginalMediaMetadata;
  /** Optional Document Type for Office Exclusive */
  docType?: string;
}

export interface MediaUploadResponse {
  uploadUrl: string;
  jobId: string;
}

export interface ServiceContextType {
  get: <T>(
    endpoint: string,
    query: Record<string, any>,
    hideError?: boolean,
    signal?: AbortSignal,
  ) => Promise<T>;
  post: <T>(
    endpoint: string,
    query: Record<string, any>,
    body: Record<string, any>,
  ) => Promise<T>;
  searchLocation: (
    query: string,
    latitude: number,
    longitude: number,
    address?: {
      houseNumber: string;
      street?: string;
      city?: string;
      county?: string;
      state?: string;
      zipcode?: string;
      unitNumber?: string;
    },
  ) => Promise<SearchAddressResult | undefined>;
  releaseListing: (listingKey: string) => Promise<any>;
  searchLocationByTaxId: (
    taxId: string,
    county?: string,
  ) => Promise<SearchAddressResult[]>;
  searchLocationByListingId: (
    listingId: string,
  ) => Promise<SearchAddressResult[]>;
  saveMedia: (preparedFiles: any, listingKey: any) => Promise<any>;
  initiateMediaUpload: (
    requests: MediaUploadRequest[],
  ) => Promise<MediaUploadResponse[]>;
  cancelMediaJob: (
    jobId: string,
    listingKey: string,
  ) => Promise<{ success: boolean; jobId: string; s3FilesDeleted: number }>;
  retryMediaJob: (
    items: Array<{ jobId: string }>,
    listingKey: string,
  ) => Promise<{ success: boolean; jobId: string; retryStrategy: string }>;
  retryAiScan: (
    jobId: string,
    listingKey: string,
    action: 'retry' | 'abort',
  ) => Promise<{
    success: boolean;
    jobId: string;
    action: string;
    message: string;
    status?: string;
    aiStatus?: string;
    aiRetryCount?: number;
  }>;
  updateMedia: (
    listingKey: string,
    mediaKey: string,
    category: string,
    updates: {
      sortOrder?: number;
      description?: string;
      longDescription?: string;
      url?: string;
      visibility?: string;
    },
  ) => Promise<{ success: boolean; mediaKey: string; message?: string }>;
  deleteMedia: (
    listingKey: string,
    results: Array<{ mediaKey: string; category: string }>,
  ) => Promise<{
    success: boolean;
    results: Array<{ mediaKey: string; success: boolean; error?: string }>;
    message?: string;
  }>;
  getSuggestions: (
    addressResult: SearchAddressResult,
  ) => Promise<RecommendationResponse>;
  deleteListingResource: (
    listingKey: string,
    resourceName: string,
    resourceKey: string,
  ) => Promise<void>;
  findAgent: (
    agentId: string,
    isScopedToBrokerage?: boolean,
    signal?: AbortSignal,
  ) => Promise<SearchAgentResult[]>;
  findAgentTeam: (memberKey: string) => Promise<SearchAgentTeamResult>;
  searchLookups: (
    lookupName: string,
    parentLookupName: string,
    parentLookupValue: string,
    filter?: string,
    signal?: AbortSignal,
  ) => Promise<any[]>;
  getListingRemarksHistory: (
    listingKey: string,
  ) => Promise<ListingRemarksHistoryResponse>;
  setGeneratedRemark: (isGenerated: boolean) => void;
  isRemarkGenerated: () => boolean;
  getListingRemarks: (
    property: { [key: string]: any },
    tone: string,
    perspective: string,
    size: string,
  ) => Promise<ListingRemarksResponse>;
  getListingResources: (listingKey: any, resourceType: any) => Promise<any>;
  getLiveValues: (
    listingKey: string,
    translate: boolean,
    fields: PropertyField[],
  ) => Promise<ProjectionResponse>;
  deleteListingResources: (
    listingKey: number,
    resources: Array<{
      type: string;
      itemNumber?: number;
      itemKey?: number;
    }>,
    useServiceLayer?: boolean,
  ) => Promise<
    Array<{
      type: string;
      itemNumber?: number;
      itemKey?: number;
      success: boolean;
      notFound?: boolean;
      error?: {
        message: string;
        code?: string;
      };
    }>
  >;
  saveListingResources: (
    listingKey: number | string,
    resources: Array<{
      resource: any;
      resourceType: string;
    }>,
  ) => Promise<
    Array<{
      resource: any;
      resourceType: string;
    }>
  >;
  resource: <T extends ResoResource>(resource: T) => ResoQueryBuilder<T>;
  executeQuery: <T extends ResoResource, R = any>(
    builder: ResoQueryBuilder<T>,
  ) => Promise<{
    value: R[];
    count?: number;
    nextLink?: string;
  }>;
  generateAIDescriptionForImage: (
    listingKey: string,
    url: string,
    maxLength?: number,
  ) => Promise<{ description: string; url: string }>;
  generateObjectsForImage: (
    listingKey: string,
    url: string,
  ) => Promise<{ description: string; url: string }>;
  generateRentSpreeLeaseLink: (payload: {
    propertyID: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    unitNumber?: string;
    agentEmail: string;
  }) => Promise<any>;
  generateShowingProviderLink: (payload: {
    property: { [key: string]: any };
  }) => Promise<any>;
  generateSupraUrl: (
    listingKey: string,
    task: SupraTask,
    memberAssociationPrimary?: string,
  ) => Promise<{ url: string }>;
  isGeneratingLeaseLink: () => boolean;
  setGeneratingLeaseLink: (isGenerating: boolean) => void;
  getAnalyticsDashboard: (
    timeframe?: string,
    refresh?: boolean,
  ) => Promise<DashboardData>;
  saveListing: (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    listingKey: string | undefined,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
    options?: { debug?: boolean },
  ) => Promise<{
    ListingKey?: string;
    ListingId?: string;
    error?: string;
    _debug?: any;
    [key: string]: any;
  }>;
  publishListing: (
    listingKey: string,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
    options?: { debug?: boolean; mediaV2?: boolean },
  ) => Promise<any>;
  getListing: (
    listingKey: string,
    options?: { mediaV2?: boolean },
  ) => Promise<{
    LiveListing: boolean;
    Property?: { [key: string]: any };
    OpenHouse?: any[];
    Media?: any[];
    Unit?: any[];
    Room?: any[];
    GreenVerification?: any[];
  }>;
  getMemberDefaults: () => Promise<MemberDefaults>;
  saveMemberDefaults: (defaults: MemberDefaults) => Promise<string | undefined>;
  deleteMemberDefaults: (
    defaults: MemberDefaults,
  ) => Promise<string | undefined>;
  getListingContacts: (
    request: ListingContactsRequest,
  ) => Promise<ListingContactsResponse>;
  sendShareDraft: (request: ShareDraftRequest) => Promise<ShareDraftResponse>;
  searchListings: (query?: string) => Promise<{
    ResponseStatus: {
      StatusCode: string;
      StatusMessage: string;
    };
    ResponseString: {
      Listings: any[];
    };
  }>;
  searchListingsPaginated: (
    request: {
      page: number;
      perPage: number;
      streamResults?: boolean;
      filters?: {
        propertyType?: string;
        mlsStatus?: string;
        search?: string;
        listingId?: string;
        taxId?: string;
        listAgentFullName?: string;
        coListAgentFullName?: string;
        streetName?: string;
        streetDirPrefix?: string;
        stateOrProvince?: string;
        postalCode?: string;
        expirationDate?: string;
        isOfficeExclusive?: string;
        isDraftOnly?: string;
        [key: string]: any;
      };
    },
    onSummary?: (response: any) => void,
    onDetail?: (response: any) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ) => Promise<void>;
  isWebSocketReady: () => boolean;
  /**
   * Create a virtual tour for a listing
   * @param listingKey - Listing identifier
   * @param name - Tour name/title
   * @param url - Tour URL
   * @returns Created media resource
   */
  saveListingTour: (
    listingKey: string,
    name: string,
    url: string,
  ) => Promise<{
    success: boolean;
    mediaKey: string;
    media: any; // MediaResource
    error?: string;
  }>;
  // Generic request operation tracking
  registerRequestOperation: (promise: Promise<any>) => void;
  isRequestInProgress: () => boolean;
  getCurrentRequestPromise: () => Promise<any> | null;
  clearRequestOperation: () => void;
  resetServiceState: () => void;
  // RESO media error handler registration
  registerResoMediaErrorHandler?: (handler: (error: Error) => void) => void;
  // Media V2 save handler registration - returns dirty media items to save (or empty array)
  registerMediaV2SaveHandler?: (handler: () => any[]) => void;
  getMediaV2SaveHandler?: () => (() => any[]) | null;
  getListingHistory: (
    listingKey: string,
    options?: { refresh?: boolean },
  ) => Promise<SystemHistoryResult>;
  saveAccount: (metadata: AccountMetadata) => Promise<SaveAccountResponse>;
}

export const ServiceContext = createContext<ServiceContextType | undefined>(
  undefined,
);

export const ServiceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { api } = useTenant();
  const { session } = useAuth();
  const { deleteListingResources: deleteResourcesFromReso } =
    useDeleteListingResources();
  const { hasFeature } = useFeatureFlags();

  const isResoMediaHandlingEnabled = hasFeature(
    FeatureFlags.NGW_MEDIA_FAILURE_MODAL,
  );
  const isEditAsFlowEnabled = hasFeature(FeatureFlags.NGW_EDIT_AS_FLOW);
  const webSocket = useWebSocketContext();
  const useWebSockets = webSocket.isEnabled;

  // Generic request operation tracking
  const currentRequestPromiseRef = useRef<Promise<any> | null>(null);
  const requestInProgressRef = useRef<boolean>(false);

  // RESO media error handler
  const [resoMediaErrorHandler, setResoMediaErrorHandler] = useState<
    ((error: Error) => void) | null
  >(null);

  // Media V2 save handler - returns dirty media items to save
  const mediaV2SaveHandlerRef = useRef<(() => any[]) | null>(null);

  const agentCache = useRef<
    Record<
      string,
      {
        timestamp: number;
        results: SearchAgentResult[];
      }
    >
  >({});

  const remarksHistoryCache = useRef<ListingRemarksHistoryResponse | undefined>(
    undefined,
  );
  const remarkGenerated = useRef<boolean>(false);
  const isGeneratingLeaseLinkRef = useRef<boolean>(false);
  const isGeneratingShowingProviderLinkRef = useRef<boolean>(false);

  const params = extractCreateParams();

  const extraPayloadValues = {
    // Add new values to SAVE and PUBLISH payload
    ...(isEditAsFlowEnabled && params.iam ? { iam: params.iam } : {}), // For edit as functionality
  };

  const setGeneratingLeaseLink = (generating: boolean) => {
    isGeneratingLeaseLinkRef.current = generating;
  };

  const setGeneratingShowingProviderLink = (generating: boolean) => {
    isGeneratingShowingProviderLinkRef.current = generating;
  };

  const generateRentSpreeLeaseLink = async (payload: {
    propertyID: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    unitNumber?: string;
    agentEmail: string;
  }) => {
    if (isGeneratingLeaseLinkRef.current) {
      throw new Error('Lease link generation already in progress');
    }

    try {
      setGeneratingLeaseLink(true);
      return await post<any>(
        'vendor/rentspree',
        {},
        {
          task: 'generate_lease',
          payload,
        },
      );
    } finally {
      setGeneratingLeaseLink(false);
    }
  };

  const generateShowingProviderLink = async (payload: {
    property: {
      [key: string]: any;
    };
  }) => {
    try {
      setGeneratingShowingProviderLink(true);
      return await post<any>(
        'vendor/showing',
        {},
        { action: 'get-showing-link', property: payload.property },
      );
    } finally {
      setGeneratingShowingProviderLink(false);
    }
  };

  const generateSupraUrl = async (
    listingKey: string,
    task: SupraTask,
    memberAssociationPrimary?: string,
  ): Promise<{ url: string }> => {
    try {
      const params: Record<string, string> = {
        serviceName: 'supra',
        task,
        listingKey,
      };
      if (memberAssociationPrimary) {
        params.memberAssociationPrimary = memberAssociationPrimary;
      }
      const result = await get<{ message: string; data: { url: string } }>(
        'vendor/lockbox',
        params,
      );
      return result.data;
    } catch (error) {
      console.error('Error generating Supra URL:', error);
      throw error;
    }
  };

  async function handleHTTPError(
    response: Response,
    hideError: boolean = false,
  ) {
    if (!response.ok) {
      let R;
      try {
        R = await response.json();
        if (!hideError) {
          console.error(R);
        }
      } catch (e) {
        R = new Error(`HTTP error! status: ${response.status}`);
      }
      if (!hideError) {
        throw R;
      }
    }
  }

  const get = async <T,>(
    endpoint: string,
    query: Record<string, any>,
    hideError: boolean = false,
    signal?: AbortSignal,
  ): Promise<T> => {
    try {
      const response = await fetch(
        toUrlFromQuery(`${api}/${endpoint}`, {
          ...query,
          system: session?.user.tenant,
        }),
        {
          credentials: 'include',
          signal,
        },
      );

      await handleHTTPError(response, hideError);

      return (await response.json()) as T;
    } catch (error) {
      // Special handling for abort errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error; // Re-throw abort errors
      }
      if (!hideError)
        // Re-throw any error that was caught, whether from the API or network
        throw error;
      return null as T;
    }
  };

  const post = async <T,>(
    endpoint: string,
    query: Record<string, any>,
    body: Record<string, any>,
    hideError: boolean = false,
  ): Promise<T> => {
    const response = await fetch(
      toUrlFromQuery(`${api}/${endpoint}`, {
        ...query,
        system: session?.user.tenant,
      }),
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
    );

    await handleHTTPError(response, hideError);

    return (await response.json()) as T;
  };

  const getAnalyticsDashboard = async (
    // Amount of time in months to scope the data too (applied within cloudwatch queries)
    timeframe: string = '6',
    // Here we can force the backend route to re-fetch the analytic data from AWS
    refresh: boolean = false,
  ): Promise<DashboardData> => {
    try {
      return await get<DashboardData>('get-analytics', {
        type: 'dashboard',
        timeframe,
        refresh: refresh ? 'true' : 'false',
      });
    } catch (error) {
      console.error('Error fetching analytics dashboard:', error);
      throw error;
    }
  };

  const searchLocationByTaxId = async (
    taxId: string,
    county?: string,
  ): Promise<SearchAddressResult[]> => {
    const sanitizedTaxId = sanitizeTaxId(taxId);

    return await get(`search-address-tax`, { taxId: sanitizedTaxId, county });
  };

  const searchLocationByListingId = async (
    listingId: string,
  ): Promise<SearchAddressResult[]> => {
    return await get(`search-address-listing-id`, { listingId });
  };

  const searchLocation = async (
    query: string,
    latitude: number,
    longitude: number,
    address?: {
      street?: string;
      houseNumber?: string;
      city?: string;
      county?: string;
      state?: string;
      zipcode?: string;
      unitNumber?: string;
    },
  ): Promise<SearchAddressResult | undefined> => {
    if (!address?.houseNumber) {
      return {
        Address: {
          Street: address?.street || '',
          City: address?.city || '',
          State: address?.state || '',
          Zip: address?.zipcode || '',
          Fips: -1,
          ParcelId: -1,
          County: address?.county || '',
          Latitude: latitude,
          Longitude: longitude,
          UnitNumber: address?.unitNumber,
        },
        Listings: [],
      };
    }
    return await get<SearchAddressResult>('search-address', {
      query,
      latitude,
      longitude,
      address_house_number: address?.houseNumber,
      address_street: address?.street,
      address_city: address?.city,
      address_county: address?.county,
      address_state: address?.state,
      address_zipcode: address?.zipcode,
      address_unit_number: address?.unitNumber,
    });
  };

  const saveMedia = async (preparedFiles: any, listingKey: any) => {
    const body = {
      listingKey: listingKey,
      translate: true,
      files: preparedFiles,
    };
    return await post('save-listing-media', {}, body);
  };

  const initiateMediaUpload = async (
    requests: MediaUploadRequest[],
  ): Promise<MediaUploadResponse[]> => {
    try {
      const response = await post<{ uploads: MediaUploadResponse[] }>(
        'media-start-upload',
        {},
        { requests },
      );
      return response.uploads;
    } catch (error) {
      console.error('Error initiating media upload:', error);
      throw error;
    }
  };

  const cancelMediaJob = async (
    jobId: string,
    listingKey: string,
  ): Promise<{ success: boolean; jobId: string; s3FilesDeleted: number }> => {
    try {
      const response = await post<{
        success: boolean;
        jobId: string;
        s3FilesDeleted: number;
      }>('media-process-cancel', {}, { jobId, listingKey });
      return response;
    } catch (error) {
      console.error('Error canceling media job:', error);
      throw error;
    }
  };

  const retryMediaJob = async (
    items: Array<{ jobId: string }>,
    listingKey: string,
  ): Promise<{ success: boolean; jobId: string; retryStrategy: string }> => {
    try {
      const response = await post<{
        success: boolean;
        jobId: string;
        retryStrategy: string;
      }>('media-process-retry', {}, { items, listingKey });
      return response;
    } catch (error) {
      console.error('Error retrying media job:', error);
      throw error;
    }
  };

  /**
   * Retry or abort AI scan for a media job
   * @param jobId - The job ID
   * @param listingKey - The listing key
   * @param action - 'retry' to retry AI scan, 'abort' to skip AI and complete
   */
  const retryAiScan = async (
    jobId: string,
    listingKey: string,
    action: 'retry' | 'abort',
  ): Promise<{
    success: boolean;
    jobId: string;
    action: string;
    message: string;
    status?: string;
    aiStatus?: string;
    aiRetryCount?: number;
  }> => {
    try {
      const response = await post<{
        success: boolean;
        jobId: string;
        action: string;
        message: string;
        status?: string;
        aiStatus?: string;
        aiRetryCount?: number;
      }>('media-process-retry-scan', {}, { jobId, listingKey, action });
      return response;
    } catch (error) {
      console.error('Error retrying AI scan:', error);
      throw error;
    }
  };

  const updateMedia = async (
    listingKey: string,
    mediaKey: string,
    category: string,
    updates: {
      sortOrder?: number;
      description?: string;
      longDescription?: string;
      url?: string;
      visibility?: string;
    },
  ): Promise<{ success: boolean; mediaKey: string; message?: string }> => {
    try {
      const response = await post<{
        success: boolean;
        mediaKey: string;
        message?: string;
      }>('media-update', {}, { listingKey, mediaKey, category, ...updates });
      return response;
    } catch (error) {
      console.error('Error updating media:', error);
      throw error;
    }
  };

  /**
   * Delete media items (supports batch delete)
   * @param listingKey - The listing key
   * @param items - Array of items to delete with mediaKey and category
   */
  const deleteMedia = async (
    listingKey: string,
    items: Array<{ mediaKey: string; category: string }>,
  ): Promise<{
    success: boolean;
    deleted: Array<{ mediaKey: string; success: boolean; error?: string }>;
    message?: string;
  }> => {
    try {
      const response = await post<{
        success: boolean;
        deleted: Array<{ mediaKey: string; success: boolean; error?: string }>;
        message?: string;
      }>('media-delete', {}, { listingKey, items });
      return response;
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  };

  /**
   * Create a virtual tour for a listing
   * @param listingKey - Listing identifier
   * @param name - Tour name/title
   * @param url - Tour URL
   */
  const saveListingTour = async (
    listingKey: string,
    name: string,
    url: string,
  ): Promise<{
    success: boolean;
    mediaKey: string;
    media: any;
    error?: string;
  }> => {
    try {
      const response = await post<{
        success: boolean;
        mediaKey: string;
        media: any;
        error?: string;
      }>('save-listing-tour', {}, { listingKey, name, url });
      return response;
    } catch (error) {
      console.error('Error creating virtual tour:', error);
      throw error;
    }
  };

  const getSuggestions = async (
    addressResult: SearchAddressResult,
  ): Promise<RecommendationResponse> => {
    const listingIds = addressResult.Listings.map(
      (listing) => listing.ListingId,
    ).join(',');

    return await get<RecommendationResponse>('get-suggestions', {
      address_street: addressResult.Address?.Street,
      address_city: addressResult.Address?.City,
      address_county: addressResult.Address?.County,
      address_state: addressResult.Address?.State,
      address_zipcode: addressResult.Address?.Zip,
      address_fips: addressResult.Address?.Fips,
      address_latitude: addressResult.Address?.Latitude,
      address_longitude: addressResult.Address?.Longitude,
      tax_id: addressResult.TaxRecord?.TaxId,
      parcel_id: addressResult.TaxRecord?.ParcelId,
      listing_ids: listingIds,
      translate: true,
    });
  };

  const executeQuery = async <T extends ResoResource, R = any>(
    builder: ResoQueryBuilder<T>,
  ): Promise<{
    value: R[];
    count?: number;
    nextLink?: string;
  }> => {
    try {
      const response = await fetch(
        `${api}/search-resource?resource=${builder.getOptions().resource}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(builder.getOptions()),
        },
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      console.error('Search resource error:', error);
      throw error;
    }
  };

  const findAgent = async (
    agentId: string,
    isScopedToBrokerage: boolean = false,
    signal?: AbortSignal,
  ): Promise<SearchAgentResult[]> => {
    // Create a unique cache key
    const cacheKey = `${agentId}-${isScopedToBrokerage}`;
    const CACHE_EXPIRATION = 20 * 60 * 1000; // 20 minutes

    // Check if result is in cache and not expired
    const cachedResult = agentCache.current[cacheKey];
    if (
      cachedResult &&
      Date.now() - cachedResult.timestamp < CACHE_EXPIRATION
    ) {
      return cachedResult.results;
    }

    // Perform the actual agent search
    const results = await get<SearchAgentResult[]>(
      'search-agent',
      {
        query: agentId,
        isScopedToBrokerage,
      },
      false,
      signal,
    );

    // Cache the results with a timestamp
    agentCache.current[cacheKey] = {
      timestamp: Date.now(),
      results,
    };

    return results;
  };

  const findAgentTeam = async (
    memberKey: string,
    signal?: AbortSignal,
  ): Promise<SearchAgentTeamResult> => {
    const results = await get<SearchAgentTeamResult>(
      'search-agent-team',
      {
        memberKey,
      },
      false,
      signal,
    );

    return results;
  };

  const searchLookups = async (
    lookupName: string,
    parentLookupName: string,
    parentLookupValue: string,
    filter?: string,
    signal?: AbortSignal,
  ): Promise<any[]> => {
    const results = await get<any[]>(
      'search-lookups',
      {
        lookupName,
        parentLookupName,
        parentLookupValue,
        filter,
      },
      false,
      signal,
    );

    return results;
  };

  const getListingRemarksHistory = async (
    listingKey: string,
  ): Promise<ListingRemarksHistoryResponse> => {
    if (remarksHistoryCache.current) {
      return Promise.resolve(remarksHistoryCache.current);
    }
    try {
      const remarks = await get<ListingRemarksHistoryResponse>(
        'get-listing-remarks-history',
        { listingKey, translate: true },
        false,
      );
      remarksHistoryCache.current = remarks;
      return remarks;
    } catch (e) {
      const remarks: ListingRemarksHistoryResponse = {
        listingid: listingKey,
        remarkshistory: [],
      };
      remarksHistoryCache.current = remarks;
      return remarks;
    }
  };

  const deleteListingResource = async (
    listingKey: string,
    resourceName: string,
    resourceKey: string,
  ): Promise<void> => {
    await get('delete-listing-resource', {
      listingKey,
      resourceName,
      resourceKey,
    });
  };

  const getListingRemarks = async (
    Property: { [key: string]: any },
    tone: string,
    perspective: string,
    size: string,
  ): Promise<ListingRemarksResponse> => {
    return await post<ListingRemarksResponse>(
      'get-listing-remarks',
      { translate: true, tone, perspective, size },
      { Property },
    );
  };

  const getListingResources = async (listingKey: any, resourceType: any) => {
    return await get<any>('get-listing-resources', {
      listingKey,
      translate: true,
      resourceType: resourceType,
    });
  };

  const getLiveValues = async (
    listingKey: string,
    translate: boolean,
    fields: PropertyField[],
  ): Promise<ProjectionResponse> => {
    const request = {
      Property: fields,
    };
    try {
      return await post<ProjectionResponse>(
        'get-listing-values',
        {
          listingKey,
          translate,
        },
        request,
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const releaseListing = async (listingKey: string) => {
    return await get('release-listing', { listingKey });
  };

  const deleteListingResources = useCallback(
    async (
      listingKey: number,
      resources: Array<{
        type: string;
        itemNumber?: number;
        itemKey?: number;
      }>,
      useServiceLayer: boolean = false,
    ) => {
      if (useServiceLayer) {
        // You can use the service layer for item number translation
        return await post<any>(
          'delete-listing-resources',
          { translate: true },
          { listingKey, resources },
        );
      }
      try {
        const results = await deleteResourcesFromReso(
          listingKey,
          resources as any,
        );
        return results;
      } catch (error) {
        console.error('Error deleting listing resources:', error);
        throw error;
      }
    },
    [deleteResourcesFromReso],
  );

  const saveListingResources = async (
    listingKey: number | string,
    resources: Array<{
      resourceType: string;
      resource?: any;
    }>,
  ) => {
    return await post<any>(
      'save-listing-resources',
      {},
      { listingKey, resources, translate: true },
    );
  };

  const generateAIDescriptionForImage = async (
    listingKey: string,
    url: string,
    maxLength: number = 50,
  ): Promise<{ description: string; url: string }> => {
    try {
      const result = await get<{ description: string; url: string }>(
        'generate-image-description',
        // Subtract an offset from the requested max length to account for potential model output variability
        // The AI_DESCRIPTION_OFFSET (10) was provided by Data Science team based on output analysis
        // of the current LLM model. This value may need adjustment if the underlying model changes
        // or if different behavior is observed in production.
        { listingKey, url, maxLength: maxLength - AI_DESCRIPTION_OFFSET },
        false,
      );

      return result;
    } catch (error) {
      console.error('Error generating AI description:', error);
      throw error;
    }
  };

  const generateObjectsForImage = async (
    listingKey: string,
    url: string,
  ): Promise<{ description: string; url: string }> => {
    try {
      const result = await get<{ description: string; url: string }>(
        'generate-image-objects',
        { listingKey, url },
        false,
      );

      return result;
    } catch (error) {
      console.error('Error generating AI Objects:', error);
      throw error;
    }
  };

  const saveListing = async (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    listingKey: string | undefined,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
    options?: { debug?: boolean },
  ): Promise<{
    ListingKey?: string;
    ListingId?: string;
    error?: string;
    _debug?: any;
    [key: string]: any;
  }> => {
    listing.Media?.forEach((media: any) => {
      if (media.MEDIA_KEY === null) {
        console.error(`MEDIA_KEY is null for save listing: ${listingKey}`);
        throw new Error(`MEDIA_KEY is null for save listing: ${listingKey}`);
      }
    });

    const savePromise = (async () => {
      try {
        const savePayload = {
          action,
          listingKey,
          listing,
          debug: options?.debug,
          ...extraPayloadValues, // For edit as functionality
        };

        if (useWebSockets) {
          return await webSocket.sendMessageAndWait(
            'LISTING.SAVE',
            savePayload,
          );
        }
        return await post<any>('save-listing', {}, savePayload);
      } catch (error) {
        console.error('saveListing failed:', error);

        if (isResoMediaHandlingEnabled && isMimeTypeNullError(error)) {
          resoMediaErrorHandler?.(error as Error);
        } else {
          throw error;
        }
      }
    })();

    // Register this request operation
    registerRequestOperation(savePromise);

    return savePromise;
  };

  const registerResoMediaErrorHandler = useCallback(
    (handler: (error: Error) => void) => {
      setResoMediaErrorHandler(() => handler);
    },
    [],
  );

  const registerMediaV2SaveHandler = useCallback((handler: () => any[]) => {
    mediaV2SaveHandlerRef.current = handler;
  }, []);

  const getMediaV2SaveHandler = useCallback(() => {
    return mediaV2SaveHandlerRef.current;
  }, []);

  const publishListing = async (
    listingKey: string,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
    options?: { debug?: boolean; mediaV2?: boolean },
  ): Promise<any> => {
    const publishPayload = {
      action: 'PUBLISH',
      listingKey,
      listing,
      debug: options?.debug,
      ...(options?.mediaV2 && { mediaV2: true }),
      ...extraPayloadValues, // For edit as functionality
    };

    if (useWebSockets) {
      return await webSocket.sendMessageAndWait(
        'LISTING.PUBLISH',
        publishPayload,
      );
    }
    return await post<any>('save-listing', {}, publishPayload);
  };

  const getListing = async (
    listingKey: string,
    options?: { mediaV2?: boolean },
  ) => {
    try {
      return await get<any>('get-listing', {
        listingKey,
        translate: true,
        checkout: true,
        ...(options?.mediaV2 && { mediaV2: true }),
      });
    } catch (error) {
      console.error('Error loading listing:', error);
      throw error;
    }
  };

  const getListingContacts = async (request: ListingContactsRequest) => {
    try {
      return await post<ListingContactsResponse>(
        'get-listing-contacts',
        {},
        request,
      );
    } catch (error) {
      console.error('Error getting listing contacts:', error);
      throw error;
    }
  };

  const sendShareDraft = async (
    request: ShareDraftRequest,
  ): Promise<ShareDraftResponse> => {
    try {
      return await post<ShareDraftResponse>('share-draft', {}, request);
    } catch (error) {
      console.error('Error sending share draft:', error);
      throw error;
    }
  };

  const getMemberDefaults = async (): Promise<MemberDefaults> => {
    try {
      const defaults = await get<MemberDefaults>('get-defaults', {});

      // remove offer management provider default if the flag is turned off
      // to prevent accidentally populating it on the Property
      if ('OFFER_MANAGEMENT_PROVIDER' in defaults) {
        if (!hasFeature(FeatureFlags.NGW_OFFER_MANAGEMENT)) {
          await deleteMemberDefaults({
            OFFER_MANAGEMENT_PROVIDER: null,
          });

          delete defaults.OFFER_MANAGEMENT_PROVIDER;
        }
      }

      return defaults;
    } catch (error) {
      console.error('Error retrieving member defaults', error);
      throw error;
    }
  };

  const saveMemberDefaults = async (
    defaults: MemberDefaults,
  ): Promise<string | undefined> => {
    try {
      const res = await post<{ message: string; results: any }>(
        'save-defaults',
        {},
        { defaults },
      );
      return res.message;
    } catch (error) {
      console.error('Error saving member defaults', error);
    }
  };

  const deleteMemberDefaults = async (
    defaults: MemberDefaults,
  ): Promise<string | undefined> => {
    try {
      const res = await post<{ message: string }>(
        'delete-defaults',
        {},
        { defaults },
      );
      return res.message;
    } catch (error) {
      console.error('Error deleting member defaults', error);
    }
  };

  const isRemarkGenerated = () => remarkGenerated.current ?? false;
  const setGeneratedRemark = (isRemarkGenerated: boolean) =>
    (remarkGenerated.current = isRemarkGenerated);

  // Generic request operation tracking functions
  const registerRequestOperation = useCallback((promise: Promise<any>) => {
    requestInProgressRef.current = true;
    currentRequestPromiseRef.current = promise;

    // Clear the operation when it completes (success or failure)
    promise.finally(() => {
      requestInProgressRef.current = false;
      currentRequestPromiseRef.current = null;
    });
  }, []);

  const isRequestInProgress = useCallback(() => {
    return requestInProgressRef.current;
  }, []);

  const getCurrentRequestPromise = useCallback(() => {
    return currentRequestPromiseRef.current;
  }, []);

  const clearRequestOperation = useCallback(() => {
    requestInProgressRef.current = false;
    currentRequestPromiseRef.current = null;
  }, []);

  const resetServiceState = useCallback(() => {
    remarkGenerated.current = false;
    remarksHistoryCache.current = undefined;
    isGeneratingLeaseLinkRef.current = false;
    isGeneratingShowingProviderLinkRef.current = false;
    clearRequestOperation();
  }, [clearRequestOperation]);

  /**
   * Get listings via WebSocket if ready, otherwise return sample data
   */
  const searchListings = useCallback(
    async (query?: string) => {
      try {
        // Check if WebSocket is connected and ready
        if (useWebSockets && webSocket.isConnected) {
          // Fetch via WebSocket
          const response = await webSocket.sendMessageAndWait(
            'LISTING.SEARCH',
            {
              query: query,
            },
            WS_SEARCH_TIMEOUT,
          );
          return response;
        } else {
          // Return sample data if WebSocket not ready or not enabled
          console.log('WebSocket not ready, using sample data');
        }
      } catch (error) {
        console.error(
          'Error fetching listings, falling back to sample data:',
          error,
        );
      }
    },
    [useWebSockets, webSocket],
  );

  /**
   * Search listings with pagination via WebSocket
   */
  const searchListingsPaginated = useCallback(
    async (
      request: {
        page: number;
        perPage: number;
        streamResults?: boolean;
        filters?: {
          propertyType?: string;
          mlsStatus?: string;
          search?: string;
          listingId?: string;
          taxId?: string;
          listAgentFullName?: string;
          coListAgentFullName?: string;
          streetName?: string;
          streetDirPrefix?: string;
          stateOrProvince?: string;
          postalCode?: string;
          expirationDate?: string;
          isOfficeExclusive?: string;
          isDraftOnly?: string;
          [key: string]: any;
        };
      },
      onSummary?: (response: any) => void,
      onDetail?: (response: any) => void,
      onComplete?: () => void,
      onError?: (error: Error) => void,
    ) => {

      try {
        // Check if WebSocket is connected and ready
        if (!useWebSockets || !webSocket.isConnected) {
          const err = new Error('WebSocket is not connected');
          console.error('WebSocket not connected for paginated search');
          if (onError) onError(err);
          throw err;
        }

        // Generate request ID
        const requestId = `LISTING.SEARCH-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Build the payload
        const payload = {
          page: request.page,
          perPage: request.perPage,
          streamResults: request.streamResults ?? true,
          filters: request.filters || {},
        };

        // Send the request with progress handlers
        await webSocket.sendMessageWithProgressHandlers(
          {
            action: 'LISTING.SEARCH',
            requestId,
            payload,
          },
          {
            onProgress: (data: any) => {
              // Route progressive responses based on status field
              if (data.status === 'listing_summary' && onSummary) {
                onSummary(data);
              } else if (data.status === 'listing_detail' && onDetail) {
                onDetail(data);
              }
            },
            onComplete: () => {
              if (onComplete) onComplete();
            },
            onError: (error: Error) => {
              console.error('Paginated listing search error:', error);
              if (onError) onError(error);
            },
          },
          WS_SEARCH_TIMEOUT,
        );
      } catch (error) {
        console.error('Error in paginated listing search:', error);
        const err =
          error instanceof Error
            ? error
            : new Error('Failed to search listings');
        if (onError) onError(err);
        throw err;
      }
    },
    [useWebSockets, webSocket],
  );

  /**
   * Check if WebSocket is connected and ready
   */
  const isWebSocketReady = useCallback(() => {
    return useWebSockets && webSocket.isConnected;
  }, [useWebSockets, webSocket.isConnected]);

  const getListingHistory = async (
    listingKey: string,
    options?: { refresh?: boolean },
  ): Promise<SystemHistoryResult> => {
    try {
      return await get<SystemHistoryResult>('get-listing-history', {
        listingKey,
        refresh: options?.refresh ? 'true' : 'false',
      });
    } catch (error) {
      console.error('Error fetching listing history:', error);
      throw error;
    }
  };

  /**
   * Update the current user's account information
   */
  const saveAccount = async (
    metadata: AccountMetadata,
  ): Promise<SaveAccountResponse> => {
    try {
      return await post<SaveAccountResponse>('save-account', {}, { metadata });
    } catch (error) {
      console.error('Error saving account:', error);
      throw error;
    }
  };

  return (
    <ServiceContext.Provider
      value={{
        get,
        post,
        searchLocation,
        searchLocationByTaxId,
        searchLocationByListingId,
        saveMedia,
        initiateMediaUpload,
        cancelMediaJob,
        retryMediaJob,
        retryAiScan,
        updateMedia,
        deleteMedia,
        saveListingTour,
        getSuggestions,
        releaseListing,
        findAgent,
        findAgentTeam,
        searchLookups,
        isRemarkGenerated,
        setGeneratedRemark,
        generateAIDescriptionForImage,
        generateObjectsForImage,
        getListingRemarksHistory,
        getListingRemarks,
        getListingResources,
        getListingContacts,
        getLiveValues,
        generateRentSpreeLeaseLink,
        generateShowingProviderLink,
        generateSupraUrl,
        isGeneratingLeaseLink: () => isGeneratingLeaseLinkRef.current,
        setGeneratingLeaseLink,
        saveListingResources,
        deleteListingResource,
        deleteListingResources,
        getAnalyticsDashboard,
        getMemberDefaults,
        saveMemberDefaults,
        deleteMemberDefaults,
        resource: resource,
        executeQuery,
        saveListing,
        publishListing,
        sendShareDraft,
        getListing,
        searchListings,
        searchListingsPaginated,
        isWebSocketReady,
        // Generic request operation tracking
        registerRequestOperation,
        isRequestInProgress,
        getCurrentRequestPromise,
        clearRequestOperation,
        resetServiceState,
        // RESO media error handler
        registerResoMediaErrorHandler,
        // Media V2 save handler
        registerMediaV2SaveHandler,
        getMediaV2SaveHandler,
        getListingHistory,
        saveAccount,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export const useService = () => {
  const context = useContext(ServiceContext);
  if (context === undefined) {
    throw new Error('useService must be used within a ServiceProvider');
  }
  return context;
};

export interface ServiceError {
  error: string;
  details?: string;
  info?: {
    code?: string;
    message?: string;
    details?: Array<{
      code: string;
      message: string;
      target: string;
    }>;
    [key: string]: any;
  };
}

export const serviceResponseMutationErrorHandler = (error: any): any => {
  // If it's an axios error with response data
  if (error.response?.data) {
    // If the response data already matches our ServiceError interface
    if (error.response.data.error) {
      throw error.response.data;
    }

    // If it's a plain error message
    const serviceError: ServiceError = {
      error: error.response.data.message || error.response.data,
      details: error.response.statusText,
      info: {
        code: error.response.status.toString(),
        message: error.response.data,
      },
    };
    throw serviceError;
  }

  // If it's a fetch response error that needs parsing
  if (error instanceof Response) {
    return error.json().then((data: any) => {
      throw data;
    });
  }

  // For any other type of error
  const serviceError: ServiceError = {
    error: error.message || 'Unknown Error',
    details: error.stack,
    info: {
      code: 'UNKNOWN',
      message: error.toString(),
    },
  };
  throw serviceError;
};

// Type guard to check if an error matches our ServiceError interface
export const isServiceError = (error: any): error is ServiceError => {
  return error && typeof error === 'object' && 'error' in error;
};
export const formatErrorDetails = (error: ServiceError): string => {
  if (error.info?.details && Array.isArray(error.info.details)) {
    return error.info.details
      .map((detail) => `${detail.message} (${detail.target})`)
      .join('\n');
  }
  return error.details || error.info?.message || error.error;
};
